import { DOCUMENT } from '@angular/common';
import { computed, DestroyRef, effect, inject, Injectable, Provider, signal, Type, untracked } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { JkitTourHelpers } from './tour.helpers';
import {
	IJkitStartTourOptions,
	IJkitTourConfig,
	IJkitTourPersistenceSavePayload,
	IJkitTourProgressRecord,
	IJkitTourResolvedRuntimeConfig,
	IJkitTourRuntimeConfig,
	IJkitTourSpotlightStyle,
	IJkitTourStep,
	IJkitTourTooltipSize,
	JKIT_TOUR_RUNTIME_CONFIG,
	JkitTourDefaultRuntimeConfig,
	resolveJkitTourRuntimeConfig,
	TJkitTourPersistenceSaveReason
} from './tour.model';
import { readJkitTourProgress, upsertJkitTourProgress, writeJkitTourProgress } from './tour.storage';

const JKIT_TOUR_START_EVENT = 'tour:start';
const JKIT_TOUR_DISMISS_EVENT = 'tour:dismiss';

interface IJkitTourStartEventDetail {
	id?: string;
	force?: boolean;
}

@Injectable()
export class JkitTourService {
	private readonly _router = inject(Router);
	private readonly _document = inject(DOCUMENT);
	private readonly _destroyRef = inject(DestroyRef);
	private readonly _config = inject(JKIT_TOUR_RUNTIME_CONFIG, { optional: true }) ?? JkitTourDefaultRuntimeConfig;
	private readonly _isBrowser = typeof window !== 'undefined';
	private readonly _cleanup: Array<() => void> = [];
	private _apiSettledTimeoutId: number | null = null;
	private _hydrateRetryTimeoutId: number | null = null;
	private _refreshRafId: number | null = null;
	private _tourStartPathname: string | null = null;
	private _isHydratingTourData = false;

	private readonly _tours = signal<readonly IJkitTourConfig[]>(this._config.tours);
	private readonly _records = signal<IJkitTourProgressRecord[]>([]);
	private readonly _activeTourId = signal<string | null>(null);
	private readonly _activeTour = signal<IJkitTourConfig | null>(null);
	private readonly _stepIndex = signal(0);
	private readonly _activeTourForce = signal(false);
	private readonly _pendingTourId = signal<string | null>(null);
	private readonly _pendingTourForce = signal(false);
	private readonly _routeUrl = signal(this._router.url || '/');
	private readonly _viewport = signal({ width: 1280, height: 720 });
	private readonly _targetRect = signal<DOMRect | null>(null);
	private readonly _tooltipSize = signal<IJkitTourTooltipSize>({ width: 320, height: 220 });
	private readonly _pendingApiRequests = signal(0);
	private readonly _isApiSettled = signal(false);
	private readonly _isTourDataReady = signal(this._config.storeTo === 'storage');
	private readonly _awaitingRuntimeAdvance = signal(false);

	private readonly _toursById = computed(() => {
		const map = new Map<string, IJkitTourConfig[]>();
		for (const tour of this._tours()) {
			const variants = map.get(tour.id);
			if (variants) {
				variants.push(tour);
			} else {
				map.set(tour.id, [tour]);
			}
		}
		return map;
	});

	public readonly tours = computed(() => this._tours());
	public readonly records = computed(() => this._records());
	public readonly activeTourId = computed(() => this._activeTourId());
	public readonly activeTour = computed(() => this._activeTour());
	public readonly texts = computed(() => this._config.texts);
	public readonly dismissible = computed(() => this._config.dismissible);
	public readonly isRunning = computed(() => !!this._activeTourId());
	public readonly stepIndex = computed(() => this._stepIndex());
	public readonly currentBreakpoint = computed(() => JkitTourHelpers.resolveBreakpoint(this._viewport().width));
	public readonly isRuntimeReady = computed(() => this._isRuntimeReady());
	public readonly isWaitingForRuntime = computed(() => this._awaitingRuntimeAdvance());
	public readonly resolvedSteps = computed(() => this._activeTour()?.steps ?? []);
	public readonly totalSteps = computed(() => this.resolvedSteps().length);
	public readonly activeStep = computed(() => this.resolvedSteps()[this._stepIndex()] ?? null);
	public readonly isProminentStep = computed(() => {
		const step = this.activeStep();
		return !!(step?.isWelcome || step?.isFinish);
	});
	public readonly isMobileStepUi = computed(() => this.currentBreakpoint() === 'mobile');
	public readonly countedSteps = computed(() => this.resolvedSteps().filter(step => !step.isWelcome && !step.isFinish));
	public readonly countedStepsTotal = computed(() => this.countedSteps().length);
	public readonly countedStepsBeforeCurrent = computed(
		() =>
			this.resolvedSteps()
				.slice(0, this._stepIndex() + 1)
				.filter(step => !step.isWelcome && !step.isFinish).length
	);
	public readonly stepBadgeLabel = computed(() => {
		const texts = this._config.texts;
		const step = this.activeStep();
		if (!step) return this._formatStepBadgeLabel(0, 0, texts.stepBadgeTemplate);
		if (step.isWelcome) return texts.stepBadgeIntro;
		if (step.isFinish) return texts.stepBadgeComplete;
		return this._formatStepBadgeLabel(
			Math.max(1, this.countedStepsBeforeCurrent()),
			Math.max(1, this.countedStepsTotal()),
			texts.stepBadgeTemplate
		);
	});
	public readonly tooltipMaxWidth = computed(() => {
		const isProminent = this.isProminentStep();
		const isMobile = this.isMobileStepUi();
		const viewportWidth = this._viewport().width;
		if (isProminent) {
			if (isMobile) return Math.max(304, viewportWidth - 24);
			return Math.max(760, Math.min(1180, viewportWidth - 80));
		}

		if (isMobile) return 276;
		return 360;
	});
	public readonly spotlightStyle = computed<IJkitTourSpotlightStyle | null>(() => {
		const step = this.activeStep();
		const rect = this._targetRect();
		const viewport = this._viewport();
		if (!step || !rect) return null;

		const pad = step.spotlightPadding ?? 10;
		const top = JkitTourHelpers.clampValue(rect.top - pad, 6, viewport.height);
		const left = JkitTourHelpers.clampValue(rect.left - pad, 6, viewport.width);
		const width = JkitTourHelpers.clampValue(rect.width + pad * 2, 0, viewport.width - left - 6);
		const height = JkitTourHelpers.clampValue(rect.height + pad * 2, 0, viewport.height - top - 6);

		return { top, left, width, height };
	});
	public readonly resolvedPlacement = computed(() =>
		JkitTourHelpers.resolvePlacement(
			this.activeStep()?.placement,
			this._targetRect(),
			this._viewport().width,
			this._viewport().height,
			this._tooltipSize(),
			this.tooltipMaxWidth()
		)
	);
	public readonly tooltipStyle = computed(() =>
		JkitTourHelpers.resolveTooltipStyle(
			this._targetRect(),
			this._viewport().width,
			this._viewport().height,
			this.resolvedPlacement(),
			this._tooltipSize(),
			this.tooltipMaxWidth()
		)
	);

	constructor() {
		this._hydrateProgressRecords();
		this._routeUrl.set(this._router.url || '/');
		this._initializeRuntime();
		this._setupEffects();
	}

	public startTour(id: string, options: IJkitStartTourOptions = {}): boolean {
		const normalizedId = id?.trim();
		if (!normalizedId) return false;
		if (this._isApiBackedMode() && !this._isTourDataReady() && !this._isHydratingTourData) {
			this._hydrateProgressRecords();
		}

		const tour = this._pickTourVariant(normalizedId);
		if (!tour || !tour.steps.length) return false;

		const shouldCheckCompletion = !this._isApiBackedMode() || this._isTourDataReady();
		if (shouldCheckCompletion && this._isTourCompleted(normalizedId) && !options.force) return false;

		const activeTour = this._activeTour();
		const activeTourId = this._activeTourId();
		if (activeTour && activeTourId !== normalizedId) {
			const incomingPriority = tour.priority ?? 0;
			const activePriority = activeTour.priority ?? 0;
			if (incomingPriority <= activePriority) return false;
		}

		const route = JkitTourHelpers.parseRouterUrl(this._routeUrl());
		if (JkitTourHelpers.matchesEntry(tour.entry, route.pathname, route.search)) {
			if (!this._isRuntimeReady()) {
				if (options.force && this._canStartBeforeReady(tour)) {
					this._activateTour(normalizedId, tour, tour.steps, { forceStart: true });
					return true;
				}

				this._setPendingTour(normalizedId, !!options.force);
				return true;
			}

			this._activateTour(normalizedId, tour, tour.steps, { forceStart: !!options.force });
			return true;
		}

		if (!options.force) return false;

		this._setPendingTour(normalizedId, !!options.force);
		const destination = JkitTourHelpers.resolveEntryPath(tour.entry);
		if (!destination) return false;
		if (destination !== this._routeUrl()) {
			void this._router.navigateByUrl(destination);
		} else {
			this._tryActivatePendingTour();
		}
		return true;
	}

	public dismissTour(): void {
		const activeTourId = this._activeTourId();
		if (!activeTourId) return;

		const now = new Date().toISOString();
		const meta = this._config.resolveRecordMeta?.() ?? null;
		const nextRecords = upsertJkitTourProgress(this._records(), activeTourId, { State: 'Completed', LastUpdated: now }, meta);
		const changedRecord = nextRecords.find(record => record.TourId === activeTourId);
		if (changedRecord) {
			this._persistRecords(nextRecords, changedRecord, 'dismissed');
		}
		this._clearActiveTourState();
	}

	public completeTour(): void {
		const activeTourId = this._activeTourId();
		if (!activeTourId) return;

		const now = new Date().toISOString();
		const meta = this._config.resolveRecordMeta?.() ?? null;
		const nextRecords = upsertJkitTourProgress(this._records(), activeTourId, { State: 'Completed', LastUpdated: now }, meta);
		const changedRecord = nextRecords.find(record => record.TourId === activeTourId);
		if (changedRecord) {
			this._persistRecords(nextRecords, changedRecord, 'completed');
		}
		this._clearActiveTourState();
	}

	public nextStep(): void {
		const total = this.totalSteps();
		if (total < 1) return;

		const step = this.activeStep();
		if (!this._isRuntimeReady() && step?.isWelcome) {
			this._awaitingRuntimeAdvance.set(true);
			return;
		}

		if (this._stepIndex() >= total - 1) {
			this.completeTour();
			return;
		}

		const nextIndex = this._stepIndex() + 1;
		this._stepIndex.set(nextIndex);
		const nextStep = this.resolvedSteps()[nextIndex] ?? null;
		if (!this._isBrowser) {
			this._primeStepTarget(nextStep);
			return;
		}

		window.requestAnimationFrame(() => {
			if (!this._activeTourId() || this._stepIndex() !== nextIndex) return;
			this._primeStepTarget(nextStep);
		});
	}

	public prevStep(): void {
		const prevIndex = Math.max(0, this._stepIndex() - 1);
		this._stepIndex.set(prevIndex);
		const prevStep = this.resolvedSteps()[prevIndex] ?? null;
		if (!this._isBrowser) {
			this._primeStepTarget(prevStep);
			return;
		}

		window.requestAnimationFrame(() => {
			if (!this._activeTourId() || this._stepIndex() !== prevIndex) return;
			this._primeStepTarget(prevStep);
		});
	}

	public updateTooltipSize(size: IJkitTourTooltipSize): void {
		if (!Number.isFinite(size.width) || !Number.isFinite(size.height)) return;
		if (size.width <= 0 || size.height <= 0) return;

		const current = this._tooltipSize();
		if (Math.abs(current.width - size.width) < 1 && Math.abs(current.height - size.height) < 1) return;
		this._tooltipSize.set(size);
	}

	private _initializeRuntime(): void {
		if (!this._isBrowser) return;

		const onWindowResize = (): void => {
			this._viewport.set({ width: window.innerWidth, height: window.innerHeight });
			this._scheduleRefreshTargetRect();
		};
		onWindowResize();
		window.addEventListener('resize', onWindowResize);
		this._cleanup.push(() => window.removeEventListener('resize', onWindowResize));

		const onWindowScroll = (): void => this._scheduleRefreshTargetRect();
		window.addEventListener('scroll', onWindowScroll, true);
		this._cleanup.push(() => window.removeEventListener('scroll', onWindowScroll, true));

		const onApiPendingChanged = (event: Event): void => {
			const customEvent = event as CustomEvent<{ pending?: number }>;
			const pendingFromEvent = customEvent.detail?.pending;
			const pendingFromGlobal = this._readPendingApiRequests();
			const pending = typeof pendingFromEvent === 'number' ? pendingFromEvent : pendingFromGlobal;
			this._setPendingApiRequests(pending);
		};
		window.addEventListener(this._config.apiPendingEventName, onApiPendingChanged as EventListener);
		this._cleanup.push(() => window.removeEventListener(this._config.apiPendingEventName, onApiPendingChanged as EventListener));

		const onTourStart = (event: Event): void => {
			const customEvent = event as CustomEvent<IJkitTourStartEventDetail>;
			if (!customEvent.detail?.id) return;
			this.startTour(customEvent.detail.id, { force: customEvent.detail.force });
		};
		const onTourDismiss = (): void => this.dismissTour();
		window.addEventListener(JKIT_TOUR_START_EVENT, onTourStart as EventListener);
		window.addEventListener(JKIT_TOUR_DISMISS_EVENT, onTourDismiss);
		this._cleanup.push(() => window.removeEventListener(JKIT_TOUR_START_EVENT, onTourStart as EventListener));
		this._cleanup.push(() => window.removeEventListener(JKIT_TOUR_DISMISS_EVENT, onTourDismiss));

		window.addEventListener('keydown', this._handleKeydown);
		this._cleanup.push(() => window.removeEventListener('keydown', this._handleKeydown));

		const routerSub = this._router.events.subscribe(event => {
			if (!(event instanceof NavigationEnd)) return;

			this._routeUrl.set(event.urlAfterRedirects || event.url || this._router.url || '/');
			this._handleRouteChangeForActiveTour();
			this._tryActivatePendingTour();
			this._scheduleRefreshTargetRect();
		});
		this._cleanup.push(() => routerSub.unsubscribe());

		this._setPendingApiRequests(this._readPendingApiRequests());

		this._destroyRef.onDestroy(() => {
			this._clearApiSettledTimer();
			this._clearHydrateRetryTimer();
			this._clearRefreshRaf();
			for (const cleanup of this._cleanup) cleanup();
			this._cleanup.length = 0;
		});
	}

	private _setupEffects(): void {
		effect(() => {
			const pendingId = this._pendingTourId();
			this._routeUrl();
			this._pendingApiRequests();
			this._isApiSettled();
			this._isTourDataReady();
			if (!pendingId) return;
			untracked(() => this._tryActivatePendingTour());
		});

		effect(() => {
			const awaiting = this._awaitingRuntimeAdvance();
			this._pendingApiRequests();
			this._isApiSettled();
			this._isTourDataReady();
			if (!awaiting || !this._isRuntimeReady()) return;
			untracked(() => {
				if (this.activeStep()?.isWelcome) {
					this._awaitingRuntimeAdvance.set(false);
					this.nextStep();
					return;
				}
				this._awaitingRuntimeAdvance.set(false);
			});
		});

		effect(() => {
			const activeTourId = this._activeTourId();
			const activeTour = this._activeTour();
			this.currentBreakpoint();
			this._pendingApiRequests();
			this._isApiSettled();
			this._isTourDataReady();
			if (!activeTourId || !activeTour) return;

			const nextVariant = this._pickTourVariant(activeTourId);
			if (!nextVariant || !nextVariant.steps.length) return;
			if (nextVariant.breakpoint === activeTour.breakpoint) return;

			if (!this._isRuntimeReady()) {
				untracked(() => {
					this._setPendingTour(activeTourId, this._activeTourForce());
					this._clearActiveTourState();
				});
				return;
			}

			untracked(() => this._activateTour(activeTourId, nextVariant, nextVariant.steps, { forceStart: this._activeTourForce() }));
		});

		effect(onCleanup => {
			const activeTourId = this._activeTourId();
			const step = this.activeStep();
			if (!activeTourId || !step) return;

			if (!step.target) {
				this._targetRect.set(null);
				return;
			}

			const target = this._document.querySelector<HTMLElement>(step.target);
			if (!target) return;

			const rect = target.getBoundingClientRect();
			const margin = step.scrollMargin ?? 96;
			const shouldScroll = rect.top < margin || rect.bottom > window.innerHeight - margin;
			if (!shouldScroll) {
				this._targetRect.set(rect);
				return;
			}

			target.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'nearest' });
			const frameId = window.requestAnimationFrame(() => this._refreshTargetRect());
			onCleanup(() => window.cancelAnimationFrame(frameId));
		});
	}

	private _handleRouteChangeForActiveTour(): void {
		const activeTourId = this._activeTourId();
		if (!activeTourId) return;

		const { pathname } = JkitTourHelpers.parseRouterUrl(this._routeUrl());
		if (!this._tourStartPathname) {
			this._tourStartPathname = pathname;
			return;
		}

		if (this._tourStartPathname !== pathname) {
			this.dismissTour();
			this._tourStartPathname = null;
		}
	}

	private _pickTourVariant(id: string): IJkitTourConfig | null {
		const variants = this._toursById().get(id);
		if (!variants?.length) return null;

		const breakpoint = this.currentBreakpoint();
		const exact = variants.find(tour => tour.breakpoint === breakpoint);
		if (exact) return exact;

		const agnostic = variants.find(tour => !tour.breakpoint);
		return agnostic ?? variants[0];
	}

	private _activateTour(id: string, tour: IJkitTourConfig, steps: readonly IJkitTourStep[], options: { forceStart?: boolean } = {}): void {
		this._pendingTourId.set(null);
		this._pendingTourForce.set(false);
		this._activeTourId.set(id);
		this._activeTour.set(tour);
		this._stepIndex.set(0);
		this._primeStepTarget(steps[0] ?? null);
		this._activeTourForce.set(!!options.forceStart);
		this._awaitingRuntimeAdvance.set(!!options.forceStart && !this._isRuntimeReady() && this._canStartBeforeReady(tour));

		const route = JkitTourHelpers.parseRouterUrl(this._routeUrl());
		this._tourStartPathname = route.pathname;
	}

	private _clearActiveTourState(): void {
		this._activeTourId.set(null);
		this._activeTour.set(null);
		this._stepIndex.set(0);
		this._targetRect.set(null);
		this._tourStartPathname = null;
		this._activeTourForce.set(false);
		this._awaitingRuntimeAdvance.set(false);
	}

	private _setPendingTour(id: string, forceStart: boolean): void {
		this._pendingTourId.set(id);
		this._pendingTourForce.set(forceStart);
	}

	private _tryActivatePendingTour(): void {
		const pendingTourId = this._pendingTourId();
		if (!pendingTourId) return;

		const pendingTour = this._pickTourVariant(pendingTourId);
		if (!pendingTour || !pendingTour.steps.length) {
			this._pendingTourId.set(null);
			this._pendingTourForce.set(false);
			return;
		}

		const route = JkitTourHelpers.parseRouterUrl(this._routeUrl());
		if (!JkitTourHelpers.matchesEntry(pendingTour.entry, route.pathname, route.search)) return;
		if (!this._isRuntimeReady()) {
			if (this._pendingTourForce() && this._canStartBeforeReady(pendingTour)) {
				this._activateTour(pendingTourId, pendingTour, pendingTour.steps, { forceStart: true });
			}
			return;
		}
		if (this._isTourCompleted(pendingTourId) && !this._pendingTourForce()) {
			this._pendingTourId.set(null);
			this._pendingTourForce.set(false);
			return;
		}

		const activeTour = this._activeTour();
		if (activeTour) {
			const activePriority = activeTour.priority ?? 0;
			const pendingPriority = pendingTour.priority ?? 0;
			if (pendingPriority <= activePriority && this._activeTourId() !== pendingTourId) {
				this._pendingTourId.set(null);
				return;
			}
		}

		this._activateTour(pendingTourId, pendingTour, pendingTour.steps, { forceStart: this._pendingTourForce() });
	}

	private _readPendingApiRequests(): number {
		if (!this._isBrowser) return 0;
		const pending = (window as unknown as Record<string, unknown>)[this._config.apiPendingGlobalKey];
		return typeof pending === 'number' ? pending : 0;
	}

	private _setPendingApiRequests(value: number): void {
		const normalized = Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
		this._pendingApiRequests.set(normalized);

		this._clearApiSettledTimer();
		if (normalized > 0) {
			this._isApiSettled.set(false);
			return;
		}

		this._apiSettledTimeoutId = window.setTimeout(() => {
			this._isApiSettled.set(true);
			this._apiSettledTimeoutId = null;
			this._tryActivatePendingTour();
		}, this._config.apiSettledDelayMs);
	}

	private _clearApiSettledTimer(): void {
		if (this._apiSettledTimeoutId === null) return;
		clearTimeout(this._apiSettledTimeoutId);
		this._apiSettledTimeoutId = null;
	}

	private _scheduleHydrateRetry(): void {
		if (this._hydrateRetryTimeoutId !== null) return;
		this._hydrateRetryTimeoutId = window.setTimeout(() => {
			this._hydrateRetryTimeoutId = null;
			if (this._isTourDataReady() || this._isHydratingTourData) return;
			this._hydrateProgressRecords();
		}, 2000);
	}

	private _clearHydrateRetryTimer(): void {
		if (this._hydrateRetryTimeoutId === null) return;
		clearTimeout(this._hydrateRetryTimeoutId);
		this._hydrateRetryTimeoutId = null;
	}

	private _isRuntimeReady(): boolean {
		if (this._isApiBackedMode()) {
			return this._isTourDataReady();
		}
		return this._pendingApiRequests() <= 0 && this._isApiSettled() && this._isTourDataReady();
	}

	private _isApiBackedMode(): boolean {
		return this._config.storeTo === 'api' || this._config.storeTo === 'mixed';
	}

	private _isTourCompleted(id: string): boolean {
		return this._records().some(entry => entry.TourId === id && entry.State === 'Completed');
	}

	private _canStartBeforeReady(tour: IJkitTourConfig): boolean {
		return !!tour.steps[0]?.isWelcome;
	}

	private _persistRecords(
		records: IJkitTourProgressRecord[],
		changedRecord: IJkitTourProgressRecord,
		reason: TJkitTourPersistenceSaveReason
	): void {
		const existed = this._records().some(record => record.TourId === changedRecord.TourId);
		this._records.set(records);

		if (this._config.storeTo === 'storage' || this._config.storeTo === 'mixed') {
			writeJkitTourProgress(this._config.storageKey, records);
		}

		if (this._config.storeTo === 'api' || this._config.storeTo === 'mixed') {
			const save = this._config.persistence?.save;
			if (!save) return;

			void Promise.resolve(
				save({
					records,
					record: changedRecord,
					reason,
					existed
				})
			).catch(() => undefined);
		}
	}

	private _primeStepTarget(step: IJkitTourStep | null): void {
		if (!step?.target) {
			this._targetRect.set(null);
			return;
		}

		const target = this._document.querySelector<HTMLElement>(step.target);
		if (!target) return;

		this._targetRect.set(target.getBoundingClientRect());
	}

	private _refreshTargetRect(): void {
		const activeStep = this.activeStep();
		if (!activeStep?.target) {
			this._targetRect.set(null);
			return;
		}

		const target = this._document.querySelector<HTMLElement>(activeStep.target);
		if (!target) return;

		this._targetRect.set(target.getBoundingClientRect());
	}

	private _scheduleRefreshTargetRect(): void {
		if (!this._activeTourId()) return;
		if (this._refreshRafId !== null) return;

		this._refreshRafId = window.requestAnimationFrame(() => {
			this._refreshRafId = null;
			this._refreshTargetRect();
		});
	}

	private _clearRefreshRaf(): void {
		if (this._refreshRafId === null) return;
		window.cancelAnimationFrame(this._refreshRafId);
		this._refreshRafId = null;
	}

	private _formatStepBadgeLabel(current: number, total: number, template: string): string {
		return template.replace(/{current}/g, `${current}`).replace(/{total}/g, `${total}`);
	}

	private readonly _handleKeydown = (event: KeyboardEvent): void => {
		if (!this._activeTourId() || !this.activeStep()) return;
		if (event.defaultPrevented) return;
		if (event.metaKey || event.ctrlKey || event.altKey) return;

		const activeElement = this._document.activeElement;
		if (activeElement) {
			const tagName = activeElement.tagName.toLowerCase();
			const isTyping = tagName === 'input' || tagName === 'textarea' || tagName === 'select';
			const isContentEditable = (activeElement as HTMLElement).isContentEditable;
			if (isTyping || isContentEditable) return;
		}

		if (event.key === 'Escape') {
			event.preventDefault();
			if (this._config.dismissible) this.dismissTour();
			return;
		}

		if (event.key === 'ArrowRight') {
			event.preventDefault();
			this.nextStep();
			return;
		}

		if (event.key === 'ArrowLeft') {
			event.preventDefault();
			this.prevStep();
			return;
		}

		if (event.key === 'Enter') {
			event.preventDefault();
			this.nextStep();
		}
	};

	private _hydrateProgressRecords(): void {
		if (this._isHydratingTourData) return;
		const mode = this._config.storeTo;
		const storageRecords = mode === 'storage' || mode === 'mixed' ? readJkitTourProgress(this._config.storageKey) : [];
		this._records.set(storageRecords);
		if (mode === 'storage') {
			this._isTourDataReady.set(true);
			this._clearHydrateRetryTimer();
			return;
		}

		const read = this._config.persistence?.read;
		if (!this._isApiBackedMode()) {
			this._isTourDataReady.set(true);
			this._clearHydrateRetryTimer();
			return;
		}

		if (!read) {
			this._isTourDataReady.set(false);
			return;
		}

		this._isHydratingTourData = true;
		void Promise.resolve(read())
			.then(records => {
				const normalizedRecords = this._normalizeProgressRecords(records);
				if (mode === 'api') {
					this._records.set(normalizedRecords);
					this._isTourDataReady.set(true);
					this._clearHydrateRetryTimer();
					return;
				}

				if (mode === 'mixed') {
					const resolvedRecords = normalizedRecords.length ? normalizedRecords : storageRecords;
					this._records.set(resolvedRecords);
					writeJkitTourProgress(this._config.storageKey, resolvedRecords);
					this._isTourDataReady.set(true);
					this._clearHydrateRetryTimer();
					return;
				}
			})
			.catch(() => {
				if (mode === 'mixed') {
					this._records.set(storageRecords);
					this._isTourDataReady.set(true);
					this._clearHydrateRetryTimer();
					return;
				}
				this._isTourDataReady.set(false);
				this._scheduleHydrateRetry();
			})
			.finally(() => {
				this._isHydratingTourData = false;
			});
	}

	private _normalizeProgressRecords(records: readonly IJkitTourProgressRecord[] | null | undefined): IJkitTourProgressRecord[] {
		if (!Array.isArray(records)) return [];

		return records
			.filter((record): record is IJkitTourProgressRecord => !!record && typeof record.TourId === 'string')
			.map(record => ({
				TourId: record.TourId,
				UserId: typeof record.UserId === 'string' ? record.UserId : undefined,
				App: typeof record.App === 'string' ? record.App : undefined,
				State: typeof record.State === 'string' ? record.State : undefined,
				LastUpdated: typeof record.LastUpdated === 'string' ? record.LastUpdated : undefined
			}));
	}
}

export interface IJKitTourPersistenceProvider {
	read(): Promise<readonly IJkitTourProgressRecord[]>;
	save(payload: IJkitTourPersistenceSavePayload): Promise<void>;
	resolveRecordMeta?: () => Partial<IJkitTourProgressRecord>;
}

export type TJkitTourConfigFactory = () => IJkitTourRuntimeConfig;

export type TJKitTourRuntimeConfig = Omit<IJkitTourRuntimeConfig, 'persistence' | 'resolveRecordMeta'> & {
	persistence?: IJkitTourRuntimeConfig['persistence'] | Type<IJKitTourPersistenceProvider>;
	resolveRecordMeta?: IJkitTourRuntimeConfig['resolveRecordMeta'];
};

export function provideJkitTour(config: IJkitTourRuntimeConfig = {}): Provider[] {
	const resolvedConfig = resolveJkitTourRuntimeConfig(config);
	return [{ provide: JKIT_TOUR_RUNTIME_CONFIG, useValue: resolvedConfig }, JkitTourService];
}

export function provideJKitTours(config: TJKitTourRuntimeConfig | TJkitTourConfigFactory = {}): Provider[] {
	if (typeof config === 'function') {
		return [
			{
				provide: JKIT_TOUR_RUNTIME_CONFIG,
				useFactory: () => resolveJkitTourRuntimeConfig(config())
			},
			JkitTourService
		];
	}

	const persistence = config.persistence;
	if (typeof persistence === 'function') {
		return [
			{
				provide: JKIT_TOUR_RUNTIME_CONFIG,
				useFactory: (): IJkitTourResolvedRuntimeConfig => {
					const persistenceService = inject(persistence);
					const { persistence: _ignored, resolveRecordMeta, ...rest } = config;
					return resolveJkitTourRuntimeConfig({
						...rest,
						persistence: {
							read: async () => persistenceService.read(),
							save: async payload => persistenceService.save(payload)
						},
						resolveRecordMeta:
							resolveRecordMeta ??
							((): Partial<IJkitTourProgressRecord> | null | undefined => persistenceService.resolveRecordMeta?.() ?? {})
					});
				}
			},
			JkitTourService
		];
	}

	return provideJkitTour(config as IJkitTourRuntimeConfig);
}

export const provideTours = provideJkitTour;
